<script setup lang="ts">
  import { computed } from 'vue'
  import type { IOption } from '@shared/types/select'
  import ArrowDown from '@shared/components/icons/arrow-down.vue'
  import CCheckbox from '@shared/components/ui/c-checkbox.vue'
  import SvgIcon from '@shared/components/svg-icon.vue'
  import CRadio from '@shared/components/ui/c-radio.vue'

  const props = defineProps<{
    modelValue?: string | string[] | number | null
    value?: string | string[] | number | null
    label?: string
    placeholder?: string
    isCheckbox?: boolean
    isRadio?: boolean
    small?: boolean
    options: IOption[]
    errorMsg?: string[]
    isFilled?: boolean
    isInvalid?: boolean
    isHideFill?: boolean
    isHideTags?: boolean
    prefixTitle?: string
    disabled?: boolean
  }>()
  const emit = defineEmits(['update:modelValue', 'change'])

  const selectVal = computed({
    get() {
      return props.modelValue || props.value
    },
    set(newVal) {
      emit('update:modelValue', newVal)
      emit('change', newVal)
    },
  })
  const selectClass = computed((): string => {
    const classList = []
    if (props.small) {
      classList.push('el-select_small')
    }
    if (props.isFilled) {
      classList.push('filled')
    }
    if (props.isHideFill) {
      classList.push('is-hide-fill')
    }
    if (props.isHideTags) {
      classList.push('is-hide-tags')
    }
    return classList.join(' ')
  })

  const hasError = computed(() => (props.errorMsg && props.errorMsg.length) || props.isInvalid)
  const prefixValue = computed(() => {
    return props.options.find(option => option.id === (selectVal.value as string[])[0])?.label || ''
  })
</script>

<template>
  <el-form-item :class="{ 'has-error': hasError }" :label="label">
    <el-select
      v-model="selectVal"
      :suffix-icon="ArrowDown"
      v-bind="$attrs"
      :placeholder="placeholder"
      :class="selectClass"
      :disabled="disabled"
    >
      <slot name="prefix" />
      <template v-if="prefixTitle" #prefix>
        <div class="tw-flex tw-items-center">
          <span
            class="tw-text-placeholder-1 tw-text-grey-800 tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap tw-max-w-[220px]"
          >
            {{ prefixTitle }} {{ prefixValue }}
          </span>
          <span
            v-if="selectVal && (selectVal as string[]).length > 1"
            class="tw-w-[25px] tw-h-[25px] tw-rounded-4 tw-bg-secondary-120 tw-text-tab-1 tw-text-primary-100 tw-flex tw-items-center tw-justify-center tw-ml-10"
          >
            +{{ (selectVal as string[]).length - 1 }}
          </span>
        </div>
      </template>
      <el-option
        v-for="item in options"
        :key="item.id"
        :disabled="item.isAdded"
        :label="item.label"
        :value="item.id"
        class="tw-flex tw-items-center"
      >
        <div v-if="isRadio">
          <c-radio v-model="selectVal" :value="(item.id as string)" :label="item.label" @click.stop />
        </div>
        <div v-if="isCheckbox">
          <c-checkbox v-model="selectVal" :label="(item.id as string)" :title="item.label" @click.stop />
        </div>
        <div v-if="item.isAdded" class="tw-flex tw-items-center tw-justify-between tw-w-full">
          <span class="tw-text-grey-600 tw-text-subtitle-1">
            {{ item.label }}
          </span>
          <span class="tw-flex tw-items-center tw-text-subtitle-1 tw-text-grey-600">
            <svg-icon name="check" size="18" class="tw-fill-grey-600 tw-mr-4" />
            Added
          </span>
        </div>
      </el-option>
    </el-select>

    <template #error="{ error }">
      <span :class="{ 'tw-hidden': errorMsg?.length }" class="tw-text-accent-500 tw-text-caption-1 tw-mt-8">
        {{ error }}
      </span>
    </template>

    <div v-if="errorMsg" class="tw-mt-8 tw-text-accent-500 tw-text-caption-1">{{ errorMsg[0] }}</div>
  </el-form-item>
</template>

<style lang="scss">
  @use '@shared/scss/ui/_forms.scss' as *;
</style>
